import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, MapPin, Clock, Award, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';

export const HomePage: React.FC = () => {
  const BASE_URL = import.meta.env.BASE_URL;

  return (
    <div className="min-h-screen">
      {/* Navjeevan Spotlight / Mission with lab image background */}
      <section className="relative py-12 sm:py-16 md:py-20 bg-neutral-900 text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
        >
          <div
            className="h-full w-full"
            style={{
              backgroundImage: `url(${BASE_URL}images/lab_equipment_3.jpg.jpg)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center right',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 via-primary-900/40 to-neutral-900/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <div className="lg:col-span-7 space-y-6">
              <p className="inline-flex items-center rounded-full bg-primary-700/40 border border-primary-500/60 px-4 py-1 text-[11px] sm:text-xs tracking-wide uppercase">
                <span className="w-2 h-2 rounded-full bg-red-400 mr-2" />
                Navjeevan Blood Center · Chaitanyapuri Metro Corridor
              </p>
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold leading-snug">
                Modern blood centre for a fast-moving Hyderabad.
              </h1>
              <p className="text-body text-neutral-200 max-w-2xl">
                At Navjeevan Blood Center, we combine compassionate care with advanced technology to keep critical blood components ready for hospitals and emergency needs across the city.
              </p>
              <div className="hidden sm:grid sm:grid-cols-3 gap-4 pt-4">
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="py-4">
                    <p className="text-xs text-neutral-300 mb-1">Years of service</p>
                    <p className="text-xl font-bold">15+ Years</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="py-4">
                    <p className="text-xs text-neutral-300 mb-1">Lives touched</p>
                    <p className="text-xl font-bold">150,000+</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="py-4">
                    <p className="text-xs text-neutral-300 mb-1">Partner hospitals</p>
                    <p className="text-xl font-bold">25+</p>
                  </CardContent>
                </Card>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Link to="/donors" state={{ scrollTo: 'register-to-donate' }}>
                  <Button size="lg" className="w-full sm:w-auto bg-white text-primary-700 hover:bg-neutral-100">
                    <Heart className="mr-2 h-5 w-5" />
                    Become a Donor
                  </Button>
                </Link>
                <Link to="/camps">
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto border-white/70 text-white hover:bg-white hover:text-primary-700"
                  >
                    Host a Blood Drive
                  </Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 mt-4 lg:mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="bg-neutral-900/80 border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center text-sm font-semibold text-white">
                      <MapPin className="h-4 w-4 mr-2 text-red-400" />
                      Easy to reach
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs text-neutral-200 space-y-1">
                    <p>Opp. Manepally Jewellers</p>
                    <p>Metro Pillar No. 1556, Chaitanyapuri</p>
                    <p>Dilsukhnagar, Hyderabad-60.</p>
                  </CardContent>
                </Card>

                <Card className="bg-neutral-900/80 border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center text-sm font-semibold text-white">
                      <Clock className="h-4 w-4 mr-2 text-red-400" />
                      Walk-in & planned camps
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs text-neutral-200 space-y-1">
                    <p>Mon - Sat: 10:00 AM - 6:00 PM</p>
                    <p>Dedicated support for corporate, college & community camps.</p>
                  </CardContent>
                </Card>

                <Card className="bg-neutral-900/80 border-white/10 sm:col-span-2">
                  <CardHeader className="pb-1">
                    <CardTitle className="flex items-center text-sm font-semibold text-white">
                      <Users className="h-4 w-4 mr-2 text-red-400" />
                      For donors, organisers & hospitals
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs text-neutral-200">
                    From first-time donors to large-scale camp organisers, our team guides you through every step of the donation and supply process.
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 mb-3">
                Our Mission: Ensuring Safe Blood for Every Need
              </h2>
              <p className="text-body mb-3 text-neutral-700">
                At Navjeevan Blood Center, we are dedicated to maintaining a safe, adequate blood supply for our community. We serve hospitals, trauma centers, and healthcare facilities with the highest standards of safety and quality.
              </p>
              <p className="text-body mb-4 text-neutral-700">
                Our comprehensive blood banking services include donor screening, blood collection, testing, processing, and distribution. We work tirelessly to ensure that life-saving blood products are available when and where they are needed most.
              </p>
            </div>
            <div className="lg:col-span-5">
              <img
                src={`${BASE_URL}images/medical_theme_1_0.png`}
                alt="Medical professionals at work"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Navjeevan - glassmorphism features */}
      <section className="pt-8 pb-12 md:pt-10 md:pb-16 bg-gradient-to-br from-neutral-900 via-primary-900 to-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
              Why donors choose Navjeevan
            </h2>
            <p className="text-body-lg text-neutral-200 max-w-2xl mx-auto">
              A city-focused blood center built around accessibility, safety and transparent communication.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card
              hover
              className="h-full bg-white/10 border border-white/20 shadow-lg shadow-black/20 rounded-2xl backdrop-blur-md transition-transform transition-shadow duration-200 hover:bg-white/15 hover:shadow-xl hover:-translate-y-0.5"
            >
              <CardContent className="pt-5 pb-6">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center mb-3">
                  <MapPin className="h-4 w-4 text-primary-100" />
                </div>
                <CardTitle className="mb-1.5 text-base sm:text-lg font-semibold text-white">
                  Prime metro location
                </CardTitle>
                <p className="text-sm text-neutral-100/90 leading-relaxed">
                  Located at Chaitanyapuri with metro connectivity and clear landmark access, making it simple for donors to reach us.
                </p>
              </CardContent>
            </Card>
            <Card
              hover
              className="h-full bg-white/10 border border-white/20 shadow-lg shadow-black/20 rounded-2xl backdrop-blur-md transition-transform transition-shadow duration-200 hover:bg-white/15 hover:shadow-xl hover:-translate-y-0.5"
            >
              <CardContent className="pt-5 pb-6">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center mb-3">
                  <Award className="h-4 w-4 text-primary-100" />
                </div>
                <CardTitle className="mb-1.5 text-base sm:text-lg font-semibold text-white">
                  Quality & safety first
                </CardTitle>
                <p className="text-sm text-neutral-100/90 leading-relaxed">
                  Rigorous testing, monitored storage and licensed operations ensure safe blood components for every patient.
                </p>
              </CardContent>
            </Card>
            <Card
              hover
              className="h-full bg-white/10 border border-white/20 shadow-lg shadow-black/20 rounded-2xl backdrop-blur-md transition-transform transition-shadow duration-200 hover:bg-white/15 hover:shadow-xl hover:-translate-y-0.5"
            >
              <CardContent className="pt-5 pb-6">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center mb-3">
                  <Users className="h-4 w-4 text-primary-100" />
                </div>
                <CardTitle className="mb-1.5 text-base sm:text-lg font-semibold text-white">
                  Donor-friendly experience
                </CardTitle>
                <p className="text-sm text-neutral-100/90 leading-relaxed">
                  Short waiting times, guided process and clear communication at every step keep the donation experience smooth.
                </p>
              </CardContent>
            </Card>
            <Card
              hover
              className="h-full bg-white/10 border border-white/20 shadow-lg shadow-black/20 rounded-2xl backdrop-blur-md transition-transform transition-shadow duration-200 hover:bg-white/15 hover:shadow-xl hover:-translate-y-0.5"
            >
              <CardContent className="pt-5 pb-6">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center mb-3">
                  <Clock className="h-4 w-4 text-primary-100" />
                </div>
                <CardTitle className="mb-1.5 text-base sm:text-lg font-semibold text-white">
                  Support for planned camps
                </CardTitle>
                <p className="text-sm text-neutral-100/90 leading-relaxed">
                  Our team helps you design, schedule and staff blood donation camps for colleges, corporates and communities.
                </p>
              </CardContent>
            </Card>
            <Card
              hover
              className="h-full bg-white/10 border border-white/20 shadow-lg shadow-black/20 rounded-2xl backdrop-blur-md transition-transform transition-shadow duration-200 hover:bg-white/15 hover:shadow-xl hover:-translate-y-0.5"
            >
              <CardContent className="pt-5 pb-6">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center mb-3">
                  <Heart className="h-4 w-4 text-primary-100" />
                </div>
                <CardTitle className="mb-1.5 text-base sm:text-lg font-semibold text-white">
                  Transparent communication
                </CardTitle>
                <p className="text-sm text-neutral-100/90 leading-relaxed">
                  We keep organisers and hospitals updated on camp performance, collected units and availability of components.
                </p>
              </CardContent>
            </Card>
            <Card
              hover
              className="h-full bg-white/10 border border-white/20 shadow-lg shadow-black/20 rounded-2xl backdrop-blur-md transition-transform transition-shadow duration-200 hover:bg-white/15 hover:shadow-xl hover:-translate-y-0.5"
            >
              <CardContent className="pt-5 pb-6">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center mb-3">
                  <CheckCircle className="h-4 w-4 text-primary-100" />
                </div>
                <CardTitle className="mb-1.5 text-base sm:text-lg font-semibold text-white">
                  Focused on long-term donors
                </CardTitle>
                <p className="text-sm text-neutral-100/90 leading-relaxed">
                  We encourage repeat donations with guidance on eligibility, reminders and comfortable follow-up experience.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Urgent Needs */}
      <section className="py-12 bg-primary-50 border-l-4 border-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-3 md:mb-0">
              <h3 className="text-subtitle font-semibold text-primary-900 mb-2">Your blood today, someone’s life tomorrow</h3>
              <p className="text-body text-primary-700">
                Your donation takes minutes, but its impact can last a lifetime.
              </p>
            </div>
            <Link to="/contact">
              <Button size="lg">
                <Heart className="mr-2 h-5 w-5" />
                Donate Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">How Blood Donation Works</h2>
            <p className="text-body-lg text-neutral-600">Simple steps to become a life-saving hero</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <Card className="text-center relative">
              <CardContent>
                <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                  1
                </div>
                <img
                  src={`${BASE_URL}images/donation_process_1_2.jpg`}
                  alt="Registration"
                  className="w-full h-40 object-cover rounded-lg mb-3"
                />
                <CardTitle>Registration</CardTitle>
                <p className="text-body text-neutral-600">
                  Sign up online or visit our center. Complete a brief health questionnaire.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center relative">
              <CardContent>
                <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <img
                  src={`${BASE_URL}images/donation_process_1_4.jpg`}
                  alt="Screening"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
                <CardTitle>Screening</CardTitle>
                <p className="text-body text-neutral-600">
                  Quick health check including temperature, pulse, and hemoglobin testing.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center relative">
              <CardContent>
                <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <img
                  src={`${BASE_URL}images/donation_process_2_2.jpg`}
                  alt="Donation"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
                <CardTitle>Donation</CardTitle>
                <p className="text-body text-neutral-600">
                  Comfortable donation process that takes about 8-10 minutes.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center relative">
              <CardContent>
                <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  4
                </div>
                <div className="w-full h-40 bg-green-100 rounded-lg mb-4 flex items-center justify-center">
                  <CheckCircle className="h-16 w-16 text-green-600" />
                </div>
                <CardTitle>Recovery</CardTitle>
                <p className="text-body text-neutral-600">
                  Rest and refreshments for 15 minutes before heading home.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-5">Ready to Make a Difference?</h2>
          <p className="text-body-lg mb-6 text-red-100">
            Join thousands of donors who are making a life-saving impact in our community.
            Your next donation could be the one that saves a life.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/donors" state={{ scrollTo: 'register-to-donate' }}>
              <Button size="lg" className="bg-white text-primary-600 hover:bg-gray-100">
                <Heart className="mr-2 h-5 w-5" />
                Register as Donor
              </Button>
            </Link>
            <Link to="/contact" state={{ scrollTo: 'contact-form' }}>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary-600">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
